import type { IntlShape } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { createADFFromHTML } from '@atlaskit/editor-common/utils/create-adf-from-html';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode/editorViewmodePluginType';
import type { PlaceholderPluginOptions } from '@atlaskit/editor-plugin-placeholder/placeholder-plugin-type';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import { i18n } from './i18n';

interface Props {
	intl: IntlShape;
	options: {
		enableLoadingSpinner?: boolean;
		isAIEnabled: boolean;
		isLivePage?: boolean;
		isMarkdownMode?: boolean;
		isPlaceholderHidden?: boolean;
		isRovoEnabled?: boolean;
		isRovoLLMEnabled: boolean;
		markdownModePlaceholder?: string;
		viewMode: ViewMode | undefined;
	};
}

export function placeholderPluginOptions({ intl, options }: Props): PlaceholderPluginOptions {
	const shouldShowSpaceShortcut =
		options.isAIEnabled && options.isRovoEnabled && fg('platform_editor_ai_aifc_streaming');
	const isMarkdownModeLivePage = options.isMarkdownMode && options.isLivePage;

	const placeholder = (() => {
		// SECTION: From confluence/next/packages/full-page-editor/src/FullPageEditorComponent.tsx `const placeholderText = `
		if (options.viewMode === 'view') {
			return undefined;
		}
		// END SECTION

		// In a Markdown Mode Live Doc the document is plain markdown, so the slash /
		// mention / AI discovery hints don't apply. Show a markdown-specific
		// placeholder instead. Scoped to Live Pages only.
		if (isMarkdownModeLivePage) {
			return options.markdownModePlaceholder || intl.formatMessage(i18n.markdownModePlaceholder);
		}

		// We disable the placeholder here becuase we want to use the new ADF placeholder, see below.
		if (shouldShowSpaceShortcut) {
			return undefined;
		}

		// SECTION: From confluence/next/packages/full-page-editor/src/FullPageEditorComponent.tsx `_getPlaceholderText()`
		if (!options.isAIEnabled) {
			return intl.formatMessage(i18n.editorEmptyDocumentPlaceholderAI);
		}

		if (editorExperiment('platform_editor_controls', 'variant1')) {
			return intl.formatMessage(i18n.defaultPlaceholder);
		}

		return intl.formatMessage(i18n.easyMentionsPlaceholder);
		// END SECTION
	})();

	const placeholderADF: DocNode | undefined = (() => {
		if (options.viewMode === 'view') {
			return undefined;
		}

		// In a Markdown Mode Live Doc we always show the plain markdown placeholder
		// text, never the Rovo space-shortcut ADF placeholder (which would take
		// precedence).
		if (isMarkdownModeLivePage) {
			return undefined;
		}

		if (shouldShowSpaceShortcut) {
			return createADFFromHTML(
				intl.formatMessage(i18n.placeholderADF, {
					code: (parts) => `<code>${parts}</code>`,
				}),
			);
		}
		return undefined;
	})();

	return {
		placeholder,
		placeholderADF,
		isPlaceholderHidden: options.isPlaceholderHidden,
		withEmptyParagraph: !isMarkdownModeLivePage && fg('platform_editor_ai_aifc_streaming'),
		isRovoLLMEnabled: options.isRovoLLMEnabled,
		enableLoadingSpinner: options.enableLoadingSpinner ?? true,
	};
}
